/**
 * Helpers for the `website_slug` field on the Institute model.
 *
 * The field accepts EITHER:
 *   1. A slug — lowercase letters / digits / hyphens — e.g. "polyaurangabad"
 *      (resolved to the internal CMS-driven public site at `/<slug>`)
 *   2. A full external URL — e.g. "https://www.mit.asia"
 *      (links open the institute's existing external site in a new tab)
 */

/** Returns true if `value` looks like an absolute URL (http or https). */
export function isExternalUrl(value) {
  if (!value || typeof value !== "string") return false;
  return /^https?:\/\//i.test(value.trim());
}

/**
 * Resolve a `website_slug` value into a navigable href.
 *   - "polyaurangabad" -> "/polyaurangabad"
 *   - "https://www.mit.asia" -> "https://www.mit.asia"
 *   - empty -> ""
 */
export function instituteHref(value) {
  const v = (value || "").trim();
  if (!v) return "";
  if (isExternalUrl(v)) return v;
  return `/${v}`;
}

/**
 * Sanitize what the user types into the Public Website field.
 * Allows full URLs (preserves http:// scheme, dots, slashes) AND slugs.
 * Detects URL-shaped input early — as soon as the typed value contains any of
 * `:`, `/` or `.`, OR starts with `http`, we preserve the value verbatim so
 * keystrokes like `https://` aren't stripped mid-typing.
 * For slug-only input, normalizes to lowercase a-z 0-9 hyphens.
 */
export function sanitizeWebsiteInput(raw) {
  const v = (raw || "").trim();
  if (!v) return "";
  if (/[:/.]/.test(v) || /^https?/i.test(v)) {
    // Treat as URL — keep as typed (the form will validate before submit).
    return v;
  }
  return v.toLowerCase().replace(/[^a-z0-9-]/g, "");
}
